import { Link } from "react-router-dom";
import { ROUTES } from "@/app/routes";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { EmptyState } from "@/components/admin/EmptyState";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/Button";
import { useContactSubmissions } from "@/features/contact/hooks";
import { useCategories } from "@/features/portfolio-categories/hooks";

export function AdminDashboardPage() {
  const categories = useCategories();
  const newSubmissions = useContactSubmissions({ page: 1, pageSize: 5, status: "NEW" });
  const categoryCount = categories.data?.length ?? 0;
  const activeCount = categories.data?.filter((category) => category.isActive).length ?? 0;
  const submissionCount = newSubmissions.data?.total ?? 0;

  return (
    <div className="space-y-7">
      <AdminPageHeader title="Dashboard" description="Operational overview for the editable Kalaco site." />
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Portfolio Categories" value={String(categoryCount)} />
        <StatCard label="Active Categories" value={String(activeCount)} />
        <StatCard label="New Submissions" value={String(submissionCount)} />
      </div>
      <section className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="rounded-md border border-cyan-300/15 bg-black/30 p-4">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="font-display text-lg font-semibold uppercase text-white">Recent Contact Submissions</h2>
            <StatusBadge tone={submissionCount > 0 ? "warning" : "muted"}>{submissionCount} New</StatusBadge>
          </div>
          {(newSubmissions.data?.items ?? []).length > 0 ? (
            <div className="space-y-3">
              {newSubmissions.data!.items.map((submission) => (
                <div key={submission.id} className="rounded-md border border-cyan-300/10 bg-black/25 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium text-white">{submission.name}</p>
                    <StatusBadge tone={submission.type === "COMMISSION" ? "warning" : "muted"}>{submission.type}</StatusBadge>
                  </div>
                  <p className="mt-1 text-sm text-content-secondary">{submission.projectName || submission.subject || submission.email}</p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title={newSubmissions.isLoading ? "Loading submissions..." : "No new submissions"} description="New contact messages and commission requests will appear here." />
          )}
        </div>
        <div className="rounded-md border border-cyan-300/15 bg-black/30 p-4">
          <h2 className="font-display text-lg font-semibold uppercase text-white">Quick Links</h2>
          <div className="mt-4 grid gap-3">
            <Button asChild variant="secondary">
              <Link to={ROUTES.admin.portfolioCategories}>Edit Categories</Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to={ROUTES.admin.socials}>Manage Social Links</Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to={ROUTES.admin.commissions}>Commission Settings</Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to={ROUTES.admin.contactSubmissions}>Contact Submissions</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, badge }: { label: string; value: string; badge?: string }) {
  return (
    <div className="rounded-md border border-cyan-300/15 bg-black/30 p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-content-secondary">{label}</p>
        {badge && <StatusBadge tone="muted">{badge}</StatusBadge>}
      </div>
      <p className="mt-4 font-display text-4xl font-bold text-white">{value}</p>
    </div>
  );
}
